"use client";

import React from "react";
import type { Template, TemplateNode } from "../types/template";
import { fonts } from "@/lib/types";
import { styleToTailwindClassName } from "./StyleEngine";
import { getVariablesFromContext, resolveVariablePath } from "./templateVariableHelpers";
import {
  resolvePath,
  resolveTemplateTokenString,
  evaluateSafeExpression,
  bindingHelpers,
  resolveBindingExpression,
} from "./templateBindings";

/**
 * Utility to convert fontFamily string to CSS variable if it matches a known font.
 * Example: "'Playfair Display', serif" => "var(--font-playfair)"
 */
export function normalizeFontFamily(fontFamily: string): string {
  if (!fontFamily || typeof fontFamily !== "string") return fontFamily;

  // Extract the first font name (remove quotes and trim)
  const match = fontFamily.match(/^['"]?([^'",]+)['"]?/);
  const fontName = match ? match[1].trim() : "";

  if (!fontName) return fontFamily;

  // Find the font in the fonts list
  const fontObj = (fonts as readonly { name: string; css: string }[]).find(
    f => f.name.toLowerCase() === fontName.toLowerCase()
  );

  if (fontObj) {
    // Convert font css to CSS variable (e.g., font-playfair => --font-playfair).
    //
    // The var() MUST carry its own fallback argument. `var(--x), 'Name', serif`
    // looks like a font stack but is not one: if `--x` is undefined, the var()
    // is invalid at computed-value time and CSS drops the *entire* font-family
    // declaration rather than moving to the next entry. The text then renders in
    // the browser default serif, which is exactly the bug D2 describes
    // (docs/qa/production-demo-readiness-2026-08-13.md) , 38 of the 97 fonts the
    // template editor offers are not instantiated in lib/template-fonts.ts, so
    // their variable never exists and every one of them silently fell back.
    //
    // With the fallback inside var(), an unloaded family degrades to the intended
    // named font and its generic (e.g. 'Macondo', cursive) instead of serif.
    return `var(--${fontObj.css}, ${fontFamily}), ${fontFamily}`;
  }

  return fontFamily;
}

export function resolveTemplateThemeMode(template: Template, isDarkMode: boolean): "light" | "dark" {
  if (template.themeBehavior === "fixed-dark") return "dark";
  if (template.themeBehavior === "fixed-light") return "light";

  const hasLightVariant = Boolean(template.themeVariants?.light);
  const hasDarkVariant = Boolean(template.themeVariants?.dark);

  // If no explicit variants exist, keep templates on light mode so global app theme
  // does not alter legacy templates unexpectedly.
  if (!hasLightVariant && !hasDarkVariant) return "light";

  if (hasLightVariant && hasDarkVariant) {
    return isDarkMode ? "dark" : "light";
  }

  if (hasDarkVariant) return "dark";
  return "light";
}

export function resolveTemplateVariables(template: Template, mode: "light" | "dark") {
  const base = template.variables || {};
  const variant = template.themeVariants?.[mode] || {};
  return {
    ...base,
    ...variant,
    colors: { ...(base.colors || {}), ...(variant.colors || {}) },
    fonts: { ...(base.fonts || {}), ...(variant.fonts || {}) },
    sizes: { ...(base.sizes || {}), ...(variant.sizes || {}) },
    images: { ...(base.images || {}), ...(variant.images || {}) },
    tokens: { ...(base.tokens || {}), ...(variant.tokens || {}) },
  };
}


export function resolveStyleBindings(style: React.CSSProperties, context: Record<string, unknown>): React.CSSProperties {
  const variables = getVariablesFromContext(context);

  const resolvedStyle: React.CSSProperties = { ...style };

  for (const key in resolvedStyle) {
    let val = resolvedStyle[key as keyof React.CSSProperties];
    if (typeof val !== "string") continue;

    // Pass 1: Resolve ${token} design-token bindings (e.g. ${colors.heading})
    if (val.includes("${")) {
      val = val.replace(/\$\{([^}]+)\}/g, (_, expr) => {
        let resolved = resolveVariablePath(variables, expr.trim());
        if (resolved === undefined && context && expr in context) {
          resolved = context[expr];
        }
        return resolved != null ? String(resolved) : "";
      });
    }

    // Pass 2: Resolve {data.path} data bindings (e.g. {page.spotlightImage})
    // Only match {word.path} patterns that were NOT already handled by ${...}
    if (val.includes("{")) {
      val = val.replace(/(?<!\$)\{([\w.[\]]+)\}/g, (_, expr) => {
        const resolved = resolvePath(context, expr.trim());
        return resolved != null ? String(resolved) : "";
      });
    }

    (resolvedStyle as Record<string, unknown>)[key as keyof React.CSSProperties] = val;
  }

  return resolvedStyle;
}

export function resolveClassNameBindings(className: string, context: Record<string, unknown>): string {
  const variables = getVariablesFromContext(context);

  if (typeof className !== "string" || !className.includes("${")) return className;

  // Replace all ${...} expressions in the className string
  return className.replace(/\$\{([^}]+)\}/g, (_, expr) => {
    let resolved = resolveVariablePath(variables, expr.trim());
    if (resolved === undefined && context && expr in context) {
      resolved = context[expr];
    }
    return resolved != null ? String(resolved) : "";
  });
}


// --- Utility: Compute Style (responsive scaling, promo, utils, etc.) ---

/**
 * Phone type ramp: maps a 1080-canvas font size to its phone-app size.
 * Piecewise-linear and monotonic — large display sizes compress hard (a 220px
 * poster headline becomes a 42px screen title) while small text barely moves
 * (17px → ~14px), which is how a real mobile hierarchy reflows. Without this
 * the legacy 0.94 font floor leaves ~200px headlines on a 360px screen.
 */
const PHONE_FONT_MIN = 12;
const PHONE_FONT_MAX = 42;
export function phoneFontRamp(px: number): number {
  const mapped = PHONE_FONT_MIN + Math.max(px - PHONE_FONT_MIN, 0) * 0.44;
  return Math.round(Math.min(Math.max(mapped, PHONE_FONT_MIN), PHONE_FONT_MAX) * 10) / 10;
}

const PHONE_SPACING_KEYS = [
  "padding", "paddingTop", "paddingBottom", "paddingLeft", "paddingRight",
  "margin", "marginTop", "marginBottom", "marginLeft", "marginRight",
  "gap", "rowGap", "columnGap",
] as const;

const PHONE_LAYOUT_KEYS = [
  "width", "height", "minWidth", "minHeight", "maxWidth", "maxHeight",
  "top", "right", "bottom", "left",
] as const;

export function computeStyle(
  node: TemplateNode,
  scale: { font: number; spacing: number; layout: number; dense?: boolean; phone?: boolean; phoneColumns?: 1 | 2 },
  scaleTexts: boolean,
  context: Record<string, unknown>,
  showPromo?: boolean,
): {style: React.CSSProperties, className?: string} {
  // Find the nearest promo in context (item, category, or parentPromo)
  const promo =
    (context?.item as Record<string, unknown> | undefined)?.promotion ||
    (context?.category as Record<string, unknown> | undefined)?.promotion ||
    context?.parentPromo;
  const promoRecord = promo as Record<string, unknown> | undefined;

  let style: React.CSSProperties = { ...(node.props?.style || {}) };

  style = resolveStyleBindings(style, context);

  if (showPromo) {
    // // Merge promoStyle if promo is active
    if (promoRecord && node.props?.promoStyle) {
      style = { ...style, ...node.props.promoStyle };
    }
    
  
    // Merge promoTypeStyles if promo is active and type matches
    if (promoRecord && node.props?.promoTypeStyles && promoRecord.discount_type) {
      const typeStyle = node.props.promoTypeStyles[promoRecord.discount_type as string];
      if (typeStyle) {
        style = { ...style, ...typeStyle };
      }
    }
  }

  style = resolveStyleBindings(style, context);

  // Capture raw values before Tailwind conversion (Tailwind strips them from
  // the style object into className). We need these for dense-split compaction.
  const rawMinWidth = style.minWidth as string | number | undefined;
  const rawDisplay = style.display as string | undefined;
  const rawFlexDirection = (style as Record<string,unknown>).flexDirection as string | undefined;
  const rawFontSize = style.fontSize as string | number | undefined;

  const extractNum = (v: unknown): number | null => {
    if (typeof v === "number") return v;
    if (typeof v === "string") {
      const m = v.match(/(\d+(?:\.\d+)?)/);
      return m ? parseFloat(m[1]) : null;
    }
    return null;
  };

  const { style:stly, className:cls } = styleToTailwindClassName(style as Parameters<typeof styleToTailwindClassName>[0]);
  style = { ...stly };
  

  // Responsive font scaling , use rawFontSize captured before Tailwind conversion
  // since styleToTailwindClassName strips fontSize into a class. Phone mode
  // skips this uniform multiply — the phone ramp below maps the authored size
  // directly so the type hierarchy compresses instead of staying flat.
  if (!scale.phone && scaleTexts && rawFontSize) {
    const rawNum = extractNum(rawFontSize);
    if (rawNum !== null) {
      style.fontSize = rawNum * scale.font;
    } else if (typeof rawFontSize === "string") {
      style.fontSize = `calc(${rawFontSize} * ${scale.font})`;
    }
  }

  // Responsive spacing scaling (phone mode compacts spacing for every node in
  // the phone pass below, so the opt-in path is desktop/tablet only)
  if (!scale.phone && node.props?.responsiveSpacing) {
    // List of spacing-related CSS properties to scale
    const spacingProps: (keyof React.CSSProperties)[] = [
      "padding", "paddingTop", "paddingBottom", "paddingLeft", "paddingRight",
      "margin", "marginTop", "marginBottom", "marginLeft", "marginRight",
      "gap", "rowGap", "columnGap",
      "borderRadius", "borderTopLeftRadius", "borderTopRightRadius",
    ];
    spacingProps.forEach(key => {
      if (style[key] !== undefined && style[key] !== null) {
        const originalValue = (style as Record<string, unknown>)[key];
        (style as Record<string, unknown>)[key] =
          typeof originalValue === "number"
            ? (originalValue * scale.spacing as number)
            : typeof originalValue === "string"
              // Optionally, handle pixel values in string form (e.g., "12px")
              ? originalValue.replace(/^(\d+(\.\d+)?)px$/, (match, num) =>
                  `${parseFloat(num) * scale.spacing}px`
                )
              : originalValue;
      }
    });
  }

  // Responsive layout scaling (width/height). Phone mode defers to the phone
  // pass below, which scales these AFTER token resolution and covers
  // min/max + absolute insets too.
  if (!scale.phone) {
    if (style.width && typeof style.width === "number") {
      style.width = style.width * scale.layout;
    } else if (typeof style.width === "string") {
      style.width = style.width.replace(/^(\d+(\.\d+)?)px$/, (_, num) => `${parseFloat(num) * scale.layout}px`);
    }
    if (style.height && typeof style.height === "number") {
      style.height = style.height * scale.layout;
    } else if (typeof style.height === "string") {
      style.height = style.height.replace(/^(\d+(\.\d+)?)px$/, (_, num) => `${parseFloat(num) * scale.layout}px`);
    }
  }

  // Animation
  if (node.props?.animate) {
    style.animationName = node.props.animate;
    style.animationDuration = "0.6s";
    style.animationFillMode = "both";
  }
  // Font family
  if (node.props?.fontFamily && !style.fontFamily) {
    style.fontFamily = normalizeFontFamily(node.props.fontFamily);
  }

  if (style.fontFamily && typeof style.fontFamily === "string") {
    style.fontFamily = normalizeFontFamily(style.fontFamily);
  }
  // Hide if show is false
  if (node.props?.show === false) style.display = "none";

  const variables = getVariablesFromContext(context);
  const s = style as Record<string, unknown>;

  // --- PROFESSIONAL STYLE BINDING RESOLUTION ---
  for (const key in style) {
    let val = s[key as keyof React.CSSProperties];
    if (typeof val === "string") {
      if (val.includes("${")) {
        const resolvedTokenValue = resolveTemplateTokenString(val, variables);
        s[key as keyof React.CSSProperties] = resolvedTokenValue;
        val = resolvedTokenValue;
      }
      const trimmed = (val as string).trim();
      // Support { ... } for JS expressions (Menuraq convention)
      if (/^\{.*\}$/.test(trimmed)) {
        const expr = trimmed.slice(1, -1);
        const result = evaluateSafeExpression(expr, { ...bindingHelpers, ...context });
        if (result === "") {
          s[key as keyof React.CSSProperties] = undefined;
        } else {
          s[key as keyof React.CSSProperties] = result;
        }
      } else if (/^\$\{.*\}$/.test(trimmed)) {
        s[key as keyof React.CSSProperties] = resolveTemplateTokenString(trimmed, variables);
      } else {
        s[key as keyof React.CSSProperties] = resolveBindingExpression(val, context);
      }
    }
  }
  // --- DENSE-SPLIT SPACING COMPACTION ---
  // In dense (phone) split-column mode, shrink token-based spacing on nodes
  // that are NOT responsiveSpacing nodes. Those tokens (e.g. `${sizes.pagePadding}`)
  // resolve to px strings and were never scaled by scale.spacing, so they keep
  // desktop-sized paddings and starve each column of width , item rows then
  // overflow and prices get clipped. Scaling only non-responsiveSpacing nodes
  // avoids double-scaling nodes already handled by the earlier spacing block.
  if (scale.dense && !node.props?.responsiveSpacing && scale.spacing !== 1) {
    const spacingProps: (keyof React.CSSProperties)[] = [
      "padding", "paddingTop", "paddingBottom", "paddingLeft", "paddingRight",
      "margin", "marginTop", "marginBottom", "marginLeft", "marginRight",
      "gap", "rowGap", "columnGap",
    ];
    spacingProps.forEach(key => {
      const value = s[key];
      if (typeof value === "number") {
        s[key] = value * scale.spacing;
      } else if (typeof value === "string" && /\d+(\.\d+)?px/.test(value)) {
        s[key] = value.replace(/(\d+(?:\.\d+)?)px/g, (_, num) =>
          `${parseFloat(num) * scale.spacing}px`
        );
      }
    });
  }

  // --- DENSE-SPLIT FONT COMPACTION ---
  // In dense split mode, item titles and category headers are still too large
  // for narrow columns (~158px). The item-head flex row is title + leader (24px)
  // + price; if the title alone is ~90px, the row overflows. Reduce font sizes
  // proportionally: category titles (>=20px) get a 0.80x multiplier, item titles
  // (>=14px) get 0.85x, and small text (<14px, descriptions) stays unchanged.
  if (scale.dense && scaleTexts && rawFontSize) {
    const current = extractNum(style.fontSize);
    if (current !== null) {
      // User hierarchy: description=xs, item-title=sm, category-title=md
      // >=30px (section/category titles) → 0.55x → md
      // >=20px (item titles) → 0.65x → sm
      // >=14px (prices, subheads) → 0.80x → readable
      // <14px (descriptions) → unchanged (xs)
      let denseFontMult = 1;
      if (current >= 30) denseFontMult = 0.55;
      else if (current >= 20) denseFontMult = 0.65;
      else if (current >= 14) denseFontMult = 0.80;
      if (denseFontMult < 1) {
        if (typeof style.fontSize === "number") {
          style.fontSize = Math.max(11, Math.round(style.fontSize * denseFontMult * 10) / 10);
        } else if (typeof style.fontSize === "string" && /\d/.test(style.fontSize)) {
          style.fontSize = style.fontSize.replace(
            /(\d+(?:\.\d+)?)/,
            (_, num) => {
              const reduced = parseFloat(num) * denseFontMult;
              return `${Math.max(11, Math.round(reduced * 10) / 10)}px`;
            },
          );
        }
      }
    }
  }
  // Shrink the dot-leader spacer so the title + leader + price row fits
  // inside narrow columns. The leader is a span with minWidth:24px in the
  // template , detect it by minWidth range and shrink it. This is OUTSIDE the
  // fontSize block because the leader has no fontSize set.
  if (scale.dense) {
    const mw = extractNum(rawMinWidth);
    if (mw !== null && mw >= 20 && mw <= 30) {
      style.minWidth = Math.max(10, Math.round(mw * 0.5));
    }
  }

  // In dense split mode, set overflow:hidden on flex rows so item-head rows
  // (title + leader + price) clip at the row edge instead of spilling past the
  // column into the viewport. Exclude the split container itself
  // (splitItemsColumns/splitCategoriesColumns) which needs overflowX:auto.
  if (scale.dense && rawDisplay === "flex"
      && !node.props?.splitItemsColumns && !node.props?.splitCategoriesColumns) {
    (style as Record<string, unknown>).overflow = "hidden";
  }

  // --- PHONE MODE (mobile-app treatment) ---
  // Below PHONE_BREAKPOINT the desktop "shrink a little" scaling produces a
  // 1080-canvas poster at ~94% type with unscaled gutters. Phone mode replaces
  // it: a type ramp that compresses the hierarchy into readable phone sizes,
  // compact spacing on every node (not just responsiveSpacing ones), layout
  // scaling for widths/heights/min-max/insets (AFTER token resolution, so
  // `${sizes.*}` values are covered), and ellipsis instead of overflow for
  // nowrap text. Structural restacking (grid collapse, split-alt stacking,
  // scatter → static grid) happens in resolveGroupLayout + the `.menuraq-phone`
  // CSS layer in app/globals.css.
  if (scale.phone) {
    const st = style as Record<string, unknown>;

    if (scaleTexts) {
      // Mirror the desktop path: `rawFontSize` is captured before
      // styleToTailwindClassName (responsive-object values get stripped into
      // className there, static px values stay inline).
      const fontSource = style.fontSize !== undefined ? style.fontSize : rawFontSize;
      const px = extractNum(fontSource);
      if (px !== null) style.fontSize = phoneFontRamp(px);
    }

    const scalePxInValue = (value: unknown, factor: number, min: number, max: number): unknown => {
      const clampOne = (n: number) => Math.min(Math.max(n * factor, min), max);
      const round = (n: number) => Math.round(n * 10) / 10;
      if (typeof value === "number") return round(clampOne(value));
      if (typeof value === "string" && /px/.test(value)) {
        return value.replace(/(\d+(?:\.\d+)?)px/g, (_, n) => `${round(clampOne(parseFloat(n)))}px`);
      }
      return value;
    };

    for (const key of PHONE_SPACING_KEYS) {
      if (st[key] != null) st[key] = scalePxInValue(st[key], scale.spacing, 4, 24);
    }
    if (st.borderRadius != null) st.borderRadius = scalePxInValue(st.borderRadius, scale.spacing, 0, 16);
    if (st.letterSpacing != null) st.letterSpacing = scalePxInValue(st.letterSpacing, 0.4, 0, 8);
    for (const key of PHONE_LAYOUT_KEYS) {
      if (st[key] != null) st[key] = scalePxInValue(st[key], scale.layout, 0, 4000);
    }

    // Each template chooses a single or two-column phone-app layout. Handle
    // both authored repeat(n, ...) and auto-fit grid forms here.
    if (typeof st.gridTemplateColumns === "string") {
      let gridTemplateColumns = st.gridTemplateColumns;
      if (/^repeat\(\s*auto-fit\s*,/i.test(gridTemplateColumns)) {
        gridTemplateColumns = `repeat(${scale.phoneColumns ?? 1}, minmax(0, 1fr))`;
      }
      const repeatMatch = gridTemplateColumns.match(/repeat\(\s*(\d+)\s*,/);
      if (repeatMatch) {
        const columns = Math.min(Number(repeatMatch[1]) || 1, scale.phoneColumns ?? 1);
        gridTemplateColumns = `repeat(${columns}, minmax(0, 1fr))`;
      }
      st.gridTemplateColumns = gridTemplateColumns;
    }

    // nowrap was a desktop typesetting trick (name …… price). On a phone the
    // name side overflows its column; make nowrap text ellipsis instead so the
    // price/leader stay on-screen. Absolute elements keep their layout (the
    // display change would break transform centring).
    const isAbsolutelyPositioned = style.position === "absolute" || style.position === "fixed";
    if (!isAbsolutelyPositioned && style.whiteSpace === "nowrap") {
      style.overflow = "hidden";
      style.textOverflow = "ellipsis";
      if (style.display === undefined) style.display = "inline-block";
      if (style.minWidth === undefined) style.minWidth = 0;
      style.maxWidth = "100%";
    }
  }

  return {style: style, className: cls };
}

// --- Utility: Text Utilities (truncate, ellipsis, utils) ---
export function applyTextUtils(text: string, node: TemplateNode): string {
  let out = text;
  if (node.props?.utils) {
    const utils = Array.isArray(node.props.utils) ? node.props.utils : [node.props.utils];
    for (const util of utils) {
      if (util === "uppercase") out = out.toUpperCase();
      if (util === "capitalize") out = out.replace(/\b\w/g, c => c.toUpperCase());
      if (util === "lowercase") out = out.toLowerCase();
      if (util === "trim") out = out.trim();
    }
  }
  if (typeof node.props?.truncate?.length === "number" && node.props.truncate.length > 0) {
    if (out.length > node.props.truncate.length) {
      out = out.slice(0, node.props.truncate.length) + (node.props.ellipsis ? "…" : "");
    }
  }
  return out;
}
